type AuthCounterMetric =
  | 'auth.refresh.success'
  | 'auth.refresh.failed'
  | 'auth.protected_call_pre_ready_blocked'
  | 'auth.401.after_refresh';

type AuthTimingMetric = 'auth.bootstrap_delay';

type AuthDiagnosticPayload = {
  metric: AuthCounterMetric | AuthTimingMetric;
  kind: 'counter' | 'timing';
  value: number;
  timestamp: string;
  meta?: Record<string, string | number | boolean>;
};

type AuthDiagnosticsState = {
  counters: Record<string, number>;
  timings: Record<string, number[]>;
  events: AuthDiagnosticPayload[];
};

declare global {
  interface Window {
    __cosellusAuthDiagnostics?: AuthDiagnosticsState;
  }
}

const randomToken = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }
  return Math.random().toString(36).slice(2, 14);
};

const getDiagnosticsState = (): AuthDiagnosticsState => {
  if (typeof window === 'undefined') {
    return { counters: {}, timings: {}, events: [] };
  }

  if (!window.__cosellusAuthDiagnostics) {
    window.__cosellusAuthDiagnostics = {
      counters: {},
      timings: {},
      events: [],
    };
  }

  return window.__cosellusAuthDiagnostics;
};

const pushEvent = (event: AuthDiagnosticPayload): void => {
  const state = getDiagnosticsState();
  state.events.push(event);
  if (state.events.length > 200) {
    state.events = state.events.slice(-200);
  }

  // Keep diagnostics structured for quick browser-level triage.
  console.info('[auth.diagnostics]', event);
};

export const recordAuthCounter = (
  metric: AuthCounterMetric,
  increment = 1,
  meta?: Record<string, string | number | boolean>,
): void => {
  const state = getDiagnosticsState();
  state.counters[metric] = (state.counters[metric] || 0) + increment;

  pushEvent({
    metric,
    kind: 'counter',
    value: increment,
    timestamp: new Date().toISOString(),
    meta,
  });
};

export const recordAuthTiming = (
  metric: AuthTimingMetric,
  valueMs: number,
  meta?: Record<string, string | number | boolean>,
): void => {
  const state = getDiagnosticsState();
  if (!state.timings[metric]) {
    state.timings[metric] = [];
  }
  state.timings[metric].push(valueMs);
  if (state.timings[metric].length > 200) {
    state.timings[metric] = state.timings[metric].slice(-200);
  }

  pushEvent({
    metric,
    kind: 'timing',
    value: valueMs,
    timestamp: new Date().toISOString(),
    meta,
  });
};

export const nextRequestGroupId = (): string => {
  const now = Date.now().toString(36);
  return `rg_${now}_${randomToken()}`;
};
